"use client";

import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import {
  CheckCircle, ArrowRight, Banknote, Clock, FileText, ShieldCheck,
  Zap, Users, Upload, X, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/axiosInstance";

const requirements = [
  { icon: FileText, title: "Valid ID", desc: "National ID, international passport, or driver's licence" },
  { icon: Banknote, title: "Proof of Income", desc: "Recent pay slip, bank statement (3 months), or business financial records" },
  { icon: Users, title: "Employment Status", desc: "Employed, self-employed, or registered business owner" },
  { icon: ShieldCheck, title: "Guarantor", desc: "At least one guarantor with verifiable income or assets" },
  { icon: Clock, title: "Credit Check", desc: "Basic credit check via your BVN (no black marks required)" },
  { icon: Zap, title: "Property Details", desc: "Proof of property ownership or a tenancy agreement of at least 12 months" },
];

const howItWorks = [
  { n: "01", title: "Fill Application", desc: "Complete the multi-step form with your personal and income details." },
  { n: "02", title: "Document Review", desc: "Our team reviews your application and documents within 1 to 4 working days." },
  { n: "03", title: "Approval & Quote", desc: "Receive a personalised financing plan with terms and monthly payments." },
  { n: "04", title: "Installation", desc: "We connect you with a verified installer and your solar system is set up." },
];

const FORM_STEPS = ["Personal Info", "Financial Details", "Documentation", "Review & Confirm"];
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
const MAX_SIZE_MB = 5;

type DocFile = { file: File; preview?: string } | null;

interface FinancingFormData {
  fullName: string; email: string; phone: string;
  fullAddress: string; employmentType: string; monthlyIncome: string;
  propertyType: string; systemSize: string; currentPowerCost: string;
  hasBvnOrNin: string; hasCacOrEmploymentLetter: string; message: string;
  hasBankStatement: string; hasIdDocument: string; hasUtilityBill: string;
  hasWorkId: string; hasAddressProof: string; hasPassportPhoto: string;
}

const DOC_FIELDS = [
  { key: "bankStatement", label: "Bank Statement (last 3 months)", required: true },
  { key: "idDocument",    label: "Government-issued ID (National ID / Passport / Driver's Licence)", required: true },
  { key: "utilityBill",   label: "Recent Utility Bill (PHCN / Water / DSTV)", required: false },
  { key: "workId",        label: "Work ID or Employment Letter", required: false },
  { key: "addressProof",  label: "Proof of Address", required: false },
  { key: "passportPhoto", label: "Recent Passport Photograph", required: false },
];

export default function FinancingPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState<FinancingFormData>({
    fullName: "", email: "", phone: "",
    fullAddress: "", employmentType: "", monthlyIncome: "",
    propertyType: "", systemSize: "", currentPowerCost: "",
    hasBvnOrNin: "", hasCacOrEmploymentLetter: "", message: "",
    hasBankStatement: "", hasIdDocument: "", hasUtilityBill: "",
    hasWorkId: "", hasAddressProof: "", hasPassportPhoto: "",
  });
  const [docs, setDocs] = useState<Record<string, DocFile>>({
    bankStatement: null, idDocument: null, utilityBill: null,
    workId: null, addressProof: null, passportPhoto: null,
  });
  const [docHasYesNo, setDocHasYesNo] = useState<Record<string, "yes" | "no">>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [docErrors, setDocErrors] = useState<Record<string, string>>({});
  const [agreedToTos, setAgreedToTos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field: keyof FinancingFormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleFileChange = useCallback((field: string, file: File | undefined) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setDocErrors(prev => ({ ...prev, [field]: "Only JPG, PNG, WEBP, or PDF files are allowed." }));
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setDocErrors(prev => ({ ...prev, [field]: `File size must not exceed ${MAX_SIZE_MB}MB.` }));
      return;
    }
    setDocErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    const isImage = file.type.startsWith("image/");
    const preview = isImage ? URL.createObjectURL(file) : undefined;
    setDocs(prev => ({ ...prev, [field]: { file, preview } }));
  }, []);

  const removeFile = (field: string) => {
    setDocs(prev => {
      const old = prev[field];
      if (old?.preview) URL.revokeObjectURL(old.preview);
      return { ...prev, [field]: null };
    });
    setDocErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (currentStep === 1) {
      if (!form.fullName.trim()) newErrors.fullName = "Full name is required.";
      if (!form.email.trim()) newErrors.email = "Email address is required.";
      else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = "Please enter a valid email address.";
      if (!form.phone.trim()) newErrors.phone = "Phone number is required.";
      if (!form.fullAddress.trim()) newErrors.fullAddress = "Full address is required.";
    }
    if (currentStep === 2) {
      if (!form.employmentType) newErrors.employmentType = "Please select your employment type.";
      if (!form.message.trim()) newErrors.message = "Additional notes are required.";
    }
    if (currentStep === 3) {
      if (!form.hasBvnOrNin) newErrors.hasBvnOrNin = "Please indicate whether you have a BVN or NIN.";
      if (!form.hasCacOrEmploymentLetter) newErrors.hasCacOrEmploymentLetter = "Please indicate whether you have a CAC certificate or employment letter.";
      const newDocErrors: Record<string, string> = {};
      DOC_FIELDS.forEach(({ key, label }) => {
        if (docHasYesNo[key] === "yes" && !docs[key]) {
          newDocErrors[key] = `${label} is required since you selected Yes.`;
        }
      });
      if (Object.keys(newDocErrors).length > 0) {
        setDocErrors(prev => ({ ...prev, ...newDocErrors }));
        return false;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goNext = () => { if (validateStep()) setCurrentStep(s => Math.min(s + 1, FORM_STEPS.length)); };
  const goBack = () => setCurrentStep(s => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    if (!agreedToTos) {
      setErrors(prev => ({ ...prev, tos: "You must agree to the Terms of Service and Privacy Policy to proceed." }));
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      // Add document Yes/No fields from docHasYesNo state
      Object.entries(docHasYesNo).forEach(([k, v]) => {
        const fieldName = `has${k.charAt(0).toUpperCase() + k.slice(1)}`;
        fd.append(fieldName, v === "yes" ? "true" : "false");
      });
      Object.entries(docs).forEach(([k, v]) => { if (v?.file) fd.append(k, v.file); });
      await api.post("/financing", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(true);
    } catch (err: any) {
      toast.error(err.response?.data?.msg || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#17BC2E]/30 focus:border-[#17BC2E] transition-all bg-white";
  const inputErrClass = "w-full px-4 py-3 border border-red-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-300/30 focus:border-red-400 transition-all bg-white";
  const labelClass = "block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2";
  const errorClass = "text-red-500 text-xs mt-1";
  const inputErr = (field: string) => errors[field] ? inputErrClass : inputClass;
  const reviewLabel = "text-xs font-semibold text-gray-400 uppercase tracking-wider";
  const reviewValue = "text-sm text-[#002301] font-medium mt-0.5";

  return (
    <div className="min-h-screen bg-[#f4f6f6]">
      {/* Hero */}
      <div className="bg-[#002301] pt-28 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <svg width="100%" height="100%">
            <defs><pattern id="fin-dots" width="30" height="30" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" /></pattern></defs>
            <rect width="100%" height="100%" fill="url(#fin-dots)" />
          </svg>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFB600]/8 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <Banknote className="w-3.5 h-3.5 text-[#FFB600]" />
              <span className="text-white/80 text-xs font-medium">Solar Financing</span>
            </span>
            <h1 className="text-white font-bold text-4xl sm:text-5xl leading-tight mb-4">
              Own Solar Today.<br /><span className="text-[#FFB600]">Pay Over Time.</span>
            </h1>
            <p className="text-white/65 text-base leading-relaxed mb-8">
              Don&apos;t let upfront costs stop you from going solar. Our flexible financing plans make clean energy accessible to every Nigerian household and business.
            </p>
            <a href="#apply" className="inline-flex items-center justify-center gap-2 bg-[#FFB600] text-[#002301] px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-[#17BC2E] hover:text-white transition-all duration-300">
              Apply Now <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2, duration: 0.6 }} className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img src="/assets/img/financing.png" alt="Solar financing" className="w-full object-cover aspect-[4/3]" />
          </motion.div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { value: "0-20%", label: "Down payment (varies by plan)" },
              { value: "24mo", label: "Repayment tenure (up to)" },
              { value: "1-4 days", label: "Application review time" },
              { value: "500+", label: "Homes financed" },
            ].map(({ value, label }) => (
              <div key={label} className="text-center py-4 px-3 rounded-xl bg-[#f4f6f6]">
                <p className="text-[#002301] font-black text-2xl sm:text-3xl mb-1">{value}</p>
                <p className="text-gray-500 text-xs leading-snug">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <p className="text-[#17BC2E] text-xs font-semibold uppercase tracking-widest mb-2">Simple Process</p>
          <h2 className="text-[#002301] font-bold text-3xl">How Financing Works</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {howItWorks.map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <p className="text-4xl font-black text-[#FFB600]/30 mb-3">{s.n}</p>
              <h3 className="font-bold text-[#002301] text-base mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Requirements */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#17BC2E] text-xs font-semibold uppercase tracking-widest mb-2">What You Need</p>
            <h2 className="text-[#002301] font-bold text-3xl">Eligibility Requirements</h2>
            <p className="text-gray-500 text-sm mt-2 max-w-lg mx-auto">We keep the requirements simple so more Nigerians can access solar financing.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {requirements.map((r, i) => (
              <motion.div key={r.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }} className="flex gap-4 p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-10 h-10 bg-[#f0f7f0] rounded-xl flex items-center justify-center flex-shrink-0">
                  <r.icon className="w-5 h-5 text-[#17BC2E]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#002301] text-sm mb-1">{r.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{r.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Multi-step Application Form */}
      <div id="apply" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {submitted ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
            <CheckCircle className="w-16 h-16 text-[#17BC2E] mx-auto mb-4" />
            <h3 className="text-[#002301] font-bold text-2xl mb-2">Application Received!</h3>
            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">Our financing team will review your application and contact you within 1 to 4 working days. Create a free account to track your application and manage your solar journey.</p>
            <Link href="/auth/register" className="inline-flex items-center gap-2 bg-[#FFB600] text-[#002301] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-[#17BC2E] hover:text-white transition-all duration-300">
              Create an Account <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Header + progress bar inside the white box */}
            <div className="px-6 sm:px-8 pt-8 pb-5 border-b border-gray-50">
              <p className="text-[#17BC2E] text-xs font-semibold uppercase tracking-widest mb-1">Get Started</p>
              <h2 className="text-[#002301] font-bold text-2xl sm:text-3xl mb-1">Apply for Financing</h2>
              <p className="text-gray-500 text-sm">Complete the steps below. Our team will respond within 1 to 4 working days.</p>
              <div className="flex items-center gap-2 mt-5 mb-1">
                {FORM_STEPS.map((label, i) => (
                  <div key={label} className="flex-1 flex flex-col items-center gap-1">
                    <div className={`w-full h-1.5 rounded-full transition-all duration-300 ${i < currentStep ? "bg-[#002301]" : "bg-gray-200"}`} />
                    <span className={`text-[10px] hidden sm:block font-medium ${i < currentStep ? "text-[#002301]" : "text-gray-400"}`}>{label}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 text-center mt-1">Step {currentStep} of {FORM_STEPS.length}</p>
            </div>

            <div className="p-6 sm:p-8">
              <AnimatePresence mode="wait">
                {/* Step 1 — Personal Info */}
                {currentStep === 1 && (
                  <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                    <h3 className="text-lg font-bold text-[#002301] mb-4">Personal Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className={labelClass}>Full Name *</label>
                        <input type="text" value={form.fullName} onChange={e => handleChange("fullName", e.target.value)} placeholder="e.g. Adebayo Fashola" className={inputErr("fullName")} />
                        {errors.fullName && <p className={errorClass}>{errors.fullName}</p>}
                      </div>
                      <div>
                        <label className={labelClass}>Email Address *</label>
                        <input type="email" value={form.email} onChange={e => handleChange("email", e.target.value)} placeholder="you@email.com" className={inputErr("email")} />
                        {errors.email && <p className={errorClass}>{errors.email}</p>}
                      </div>
                      <div>
                        <label className={labelClass}>Phone Number *</label>
                        <input type="tel" value={form.phone} onChange={e => handleChange("phone", e.target.value)} placeholder="+234 8xx xxx xxxx" className={inputErr("phone")} />
                        {errors.phone && <p className={errorClass}>{errors.phone}</p>}
                      </div>
                      <div>
                        <label className={labelClass}>Full Address *</label>
                        <input type="text" value={form.fullAddress} onChange={e => handleChange("fullAddress", e.target.value)} placeholder="House no., street, city, state" className={inputErr("fullAddress")} />
                        {errors.fullAddress && <p className={errorClass}>{errors.fullAddress}</p>}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 2 — Financial Details */}
                {currentStep === 2 && (
                  <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                    <h3 className="text-lg font-bold text-[#002301] mb-4">Financial Details</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className={labelClass}>Employment Type *</label>
                        <select value={form.employmentType} onChange={e => handleChange("employmentType", e.target.value)} className={inputErr("employmentType")}>
                          <option value="">Select employment type</option>
                          <option>Salaried Employee</option>
                          <option>Self Employed</option>
                          <option>Business Owner</option>
                          <option>Government Worker</option>
                          <option>Retired</option>
                        </select>
                        {errors.employmentType && <p className={errorClass}>{errors.employmentType}</p>}
                      </div>
                      <div>
                        <label className={labelClass}>Monthly Income</label>
                        <select value={form.monthlyIncome} onChange={e => handleChange("monthlyIncome", e.target.value)} className={inputClass}>
                          <option value="">Select income range</option>
                          <option>Below &#8358;100,000</option>
                          <option>&#8358;100,000 - &#8358;300,000</option>
                          <option>&#8358;300,000 - &#8358;600,000</option>
                          <option>&#8358;600,000 - &#8358;1,000,000</option>
                          <option>Above &#8358;1,000,000</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Monthly Power Cost</label>
                        <select value={form.currentPowerCost} onChange={e => handleChange("currentPowerCost", e.target.value)} className={inputClass}>
                          <option value="">Select power cost range</option>
                          <option>Below &#8358;20,000</option>
                          <option>&#8358;20,000 - &#8358;50,000</option>
                          <option>&#8358;50,000 - &#8358;100,000</option>
                          <option>&#8358;100,000 - &#8358;200,000</option>
                          <option>Above &#8358;200,000</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Property Type</label>
                        <select value={form.propertyType} onChange={e => handleChange("propertyType", e.target.value)} className={inputClass}>
                          <option value="">Select property type</option>
                          <option>Owned Home</option>
                          <option>Rented Property</option>
                          <option>Commercial Property</option>
                          <option>Government Estate</option>
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelClass}>Estimated System Size Needed</label>
                        <select value={form.systemSize} onChange={e => handleChange("systemSize", e.target.value)} className={inputClass}>
                          <option value="">Not sure yet</option>
                          <option>1-3kW (Small Home / Apartment)</option>
                          <option>3-5kW (Medium Home)</option>
                          <option>5-10kW (Large Home / Small Office)</option>
                          <option>10kW+ (Large Commercial)</option>
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelClass}>Additional Notes *</label>
                        <textarea value={form.message} onChange={e => handleChange("message", e.target.value)} rows={4} placeholder="Tell us why you should be considered for financing. Include your situation, how you plan to use the system, and anything that supports your application." className={inputErr("message")} />
                        {errors.message && <p className={errorClass}>{errors.message}</p>}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 3 — Documentation (merged questions + uploads with Yes/No) */}
                {currentStep === 3 && (
                  <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                    <h3 className="text-lg font-bold text-[#002301] mb-4">Documentation</h3>
                    <p className="text-sm text-gray-500 mb-4">For each document, select Yes to upload now or No to send manually after approval.</p>
                    
                    {/* BVN/NIN and CAC/Employment Letter questions */}
                    <div className="space-y-4">
                      <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                        <label className={labelClass}>Do you have a BVN or NIN? *</label>
                        <p className="text-xs text-gray-500 mb-3">Required for identity verification and credit assessment.</p>
                        <div className="flex gap-4">
                          {["yes", "no"].map(v => (
                            <label key={v} className={`flex-1 flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${form.hasBvnOrNin === v ? "border-[#002301] bg-white" : "border-gray-200 hover:border-gray-300"}`}>
                              <input type="radio" name="hasBvnOrNin" value={v} checked={form.hasBvnOrNin === v} onChange={() => handleChange("hasBvnOrNin", v)} className="sr-only" />
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.hasBvnOrNin === v ? "border-[#002301]" : "border-gray-300"}`}>
                                {form.hasBvnOrNin === v && <div className="w-2 h-2 rounded-full bg-[#002301]" />}
                              </div>
                              <span className="text-sm font-medium text-gray-700">{v === "yes" ? "Yes, I have one" : "No, I don't"}</span>
                            </label>
                          ))}
                        </div>
                        {errors.hasBvnOrNin && <p className={errorClass}>{errors.hasBvnOrNin}</p>}
                      </div>
                      <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                        <label className={labelClass}>Do you have a CAC certificate or employment letter? *</label>
                        <p className="text-xs text-gray-500 mb-3">For business owners (CAC) or salary earners (employment letter).</p>
                        <div className="flex gap-4">
                          {["yes", "no"].map(v => (
                            <label key={v} className={`flex-1 flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${form.hasCacOrEmploymentLetter === v ? "border-[#002301] bg-white" : "border-gray-200 hover:border-gray-300"}`}>
                              <input type="radio" name="hasCacOrEmploymentLetter" value={v} checked={form.hasCacOrEmploymentLetter === v} onChange={() => handleChange("hasCacOrEmploymentLetter", v)} className="sr-only" />
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.hasCacOrEmploymentLetter === v ? "border-[#002301]" : "border-gray-300"}`}>
                                {form.hasCacOrEmploymentLetter === v && <div className="w-2 h-2 rounded-full bg-[#002301]" />}
                              </div>
                              <span className="text-sm font-medium text-gray-700">{v === "yes" ? "Yes, I have one" : "No, I don't"}</span>
                            </label>
                          ))}
                        </div>
                        {errors.hasCacOrEmploymentLetter && <p className={errorClass}>{errors.hasCacOrEmploymentLetter}</p>}
                      </div>
                    </div>

                    {/* Document uploads with Yes/No toggles */}
                    <div className="space-y-3">
                      {DOC_FIELDS.map(({ key, label }) => {
                        const doc = docs[key];
                        const hasYesNo = docHasYesNo[key];
                        return (
                          <div key={key} className={`p-4 rounded-xl border transition-colors ${docErrors[key] ? "border-red-300 bg-red-50/30" : "border-gray-200"}`}>
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{label}</span>
                              {doc && (
                                <button type="button" onClick={() => removeFile(key)} className="text-red-400 hover:text-red-600 transition-colors">
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                            
                            {/* Yes/No toggle */}
                            <div className="flex gap-3 mb-3">
                              {["yes", "no"].map(v => (
                                <label key={v} className={`flex-1 flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${hasYesNo === v ? "border-[#002301] bg-[#002301]/5" : "border-gray-200 hover:border-gray-300"}`}>
                                  <input type="radio" name={`doc-${key}`} value={v} checked={hasYesNo === v} onChange={() => setDocHasYesNo(prev => ({ ...prev, [key]: v as "yes" | "no" }))} className="sr-only" />
                                  <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${hasYesNo === v ? "border-[#002301]" : "border-gray-300"}`}>
                                    {hasYesNo === v && <div className="w-1.5 h-1.5 rounded-full bg-[#002301]" />}
                                  </div>
                                  <span className="text-xs font-medium text-gray-700">{v === "yes" ? "Upload now" : "Send manually"}</span>
                                </label>
                              ))}
                            </div>

                            {/* Upload area (only shown if Yes) */}
                            {hasYesNo === "yes" && (
                              <>
                                {doc ? (
                                  <div className="flex items-center gap-3">
                                    {doc.preview ? (
                                      <img src={doc.preview} alt="preview" className="w-14 h-14 object-cover rounded-lg border" />
                                    ) : (
                                      <div className="w-14 h-14 bg-[#f0f7f0] rounded-lg border flex items-center justify-center">
                                        <FileText className="w-6 h-6 text-[#17BC2E]" />
                                      </div>
                                    )}
                                    <div>
                                      <p className="text-sm font-medium text-gray-800 truncate max-w-[200px]">{doc.file.name}</p>
                                      <p className="text-xs text-gray-400">{(doc.file.size / 1024 / 1024).toFixed(2)} MB</p>
                                    </div>
                                  </div>
                                ) : (
                                  <label className="cursor-pointer group">
                                    <div className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-200 rounded-xl group-hover:border-[#17BC2E]/50 transition-colors">
                                      <Upload className="w-4 h-4 text-gray-400 group-hover:text-[#17BC2E]" />
                                      <span className="text-sm text-gray-400 group-hover:text-[#17BC2E]">Click to upload</span>
                                    </div>
                                    <input type="file" accept={ALLOWED_TYPES.join(",")} onChange={e => handleFileChange(key, e.target.files?.[0])} className="hidden" />
                                  </label>
                                )}
                                {docErrors[key] && <p className={errorClass}>{docErrors[key]}</p>}
                              </>
                            )}

                            {/* Manual send message (only shown if No) */}
                            {hasYesNo === "no" && (
                              <p className="text-xs text-gray-500 italic">You will send this document manually after approval.</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {/* Step 4 — Review & Confirm */}
                {currentStep === 4 && (
                  <motion.div key="step5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                    <div>
                      <h3 className="text-lg font-bold text-[#002301]">Review Your Application</h3>
                      <p className="text-sm text-gray-500 mt-1">Please review your information before submitting.</p>
                    </div>

                    {/* Personal Info */}
                    <div className="rounded-xl border border-gray-100 overflow-hidden">
                      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                        <span className="text-xs font-bold text-[#002301] uppercase tracking-wider">Personal Information</span>
                        <button type="button" onClick={() => setCurrentStep(1)} className="text-xs text-[#17BC2E] font-semibold hover:underline">Edit</button>
                      </div>
                      <div className="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><p className={reviewLabel}>Full Name</p><p className={reviewValue}>{form.fullName || <span className="text-gray-400 italic text-sm font-normal">Not provided</span>}</p></div>
                        <div><p className={reviewLabel}>Email</p><p className={reviewValue}>{form.email || <span className="text-gray-400 italic text-sm font-normal">Not provided</span>}</p></div>
                        <div><p className={reviewLabel}>Phone</p><p className={reviewValue}>{form.phone || <span className="text-gray-400 italic text-sm font-normal">Not provided</span>}</p></div>
                        <div><p className={reviewLabel}>Address</p><p className={reviewValue}>{form.fullAddress || <span className="text-gray-400 italic text-sm font-normal">Not provided</span>}</p></div>
                      </div>
                    </div>

                    {/* Financial Details */}
                    <div className="rounded-xl border border-gray-100 overflow-hidden">
                      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                        <span className="text-xs font-bold text-[#002301] uppercase tracking-wider">Financial Details</span>
                        <button type="button" onClick={() => setCurrentStep(2)} className="text-xs text-[#17BC2E] font-semibold hover:underline">Edit</button>
                      </div>
                      <div className="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><p className={reviewLabel}>Employment Type</p><p className={reviewValue}>{form.employmentType || <span className="text-gray-400 italic text-sm font-normal">Not provided</span>}</p></div>
                        <div><p className={reviewLabel}>Monthly Income</p><p className={reviewValue}>{form.monthlyIncome || <span className="text-gray-400 italic text-sm font-normal">Not provided</span>}</p></div>
                        <div><p className={reviewLabel}>Monthly Power Cost</p><p className={reviewValue}>{form.currentPowerCost || <span className="text-gray-400 italic text-sm font-normal">Not provided</span>}</p></div>
                        <div><p className={reviewLabel}>Property Type</p><p className={reviewValue}>{form.propertyType || <span className="text-gray-400 italic text-sm font-normal">Not provided</span>}</p></div>
                        <div className="sm:col-span-2"><p className={reviewLabel}>System Size</p><p className={reviewValue}>{form.systemSize || <span className="text-gray-400 italic text-sm font-normal">Not sure yet</span>}</p></div>
                        <div className="sm:col-span-2"><p className={reviewLabel}>Additional Notes</p><p className="text-sm text-[#002301] mt-0.5 whitespace-pre-wrap">{form.message || <span className="text-gray-400 italic font-normal">Not provided</span>}</p></div>
                      </div>
                    </div>

                    {/* Documentation */}
                    <div className="rounded-xl border border-gray-100 overflow-hidden">
                      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                        <span className="text-xs font-bold text-[#002301] uppercase tracking-wider">Documentation</span>
                        <button type="button" onClick={() => setCurrentStep(3)} className="text-xs text-[#17BC2E] font-semibold hover:underline">Edit</button>
                      </div>
                      <div className="px-4 py-4 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div><p className={reviewLabel}>Has BVN / NIN</p><p className={reviewValue}>{form.hasBvnOrNin === "yes" ? "Yes" : form.hasBvnOrNin === "no" ? "No" : <span className="text-gray-400 italic text-sm font-normal">Not answered</span>}</p></div>
                          <div><p className={reviewLabel}>Has CAC / Employment Letter</p><p className={reviewValue}>{form.hasCacOrEmploymentLetter === "yes" ? "Yes" : form.hasCacOrEmploymentLetter === "no" ? "No" : <span className="text-gray-400 italic text-sm font-normal">Not answered</span>}</p></div>
                        </div>
                        <div className="border-t border-gray-100 pt-3">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Documents</p>
                          <div className="space-y-2">
                            {DOC_FIELDS.map(({ key, label }) => {
                              const hasYesNo = docHasYesNo[key];
                              const doc = docs[key];
                              return (
                                <div key={key} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                                  <span className="text-xs text-gray-500">{label}</span>
                                  {!hasYesNo ? (
                                    <span className="text-xs text-gray-400 italic">Not selected</span>
                                  ) : hasYesNo === "yes" ? (
                                    doc ? (
                                      <span className="flex items-center gap-1.5 text-xs font-medium text-[#17BC2E]">
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        {doc.file.name.length > 22 ? doc.file.name.slice(0, 22) + "..." : doc.file.name}
                                      </span>
                                    ) : (
                                      <span className="text-xs text-orange-500 italic">Pending upload</span>
                                    )
                                  ) : (
                                    <span className="text-xs text-gray-500 italic">Send manually</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ToS checkbox */}
                    <div className={`p-4 rounded-xl border-2 transition-colors ${errors.tos ? "border-red-300 bg-red-50/30" : agreedToTos ? "border-[#002301] bg-[#f0f7f0]" : "border-gray-200 bg-gray-50"}`}>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <div className="relative mt-0.5 flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={agreedToTos}
                            onChange={e => {
                              setAgreedToTos(e.target.checked);
                              if (e.target.checked) setErrors(prev => { const n = { ...prev }; delete n.tos; return n; });
                            }}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${agreedToTos ? "bg-[#002301] border-[#002301]" : "border-gray-300 bg-white"}`}>
                            {agreedToTos && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          I have read and agree to Ren Power Group&apos;s{" "}
                          <Link href="/terms-of-service" target="_blank" className="text-[#17BC2E] font-semibold hover:underline">Terms of Service</Link>
                          {" "}and{" "}
                          <Link href="/privacy-policy" target="_blank" className="text-[#17BC2E] font-semibold hover:underline">Privacy Policy</Link>.
                          I confirm that all information provided is accurate and complete.
                        </p>
                      </label>
                      {errors.tos && <p className={errorClass + " mt-2"}>{errors.tos}</p>}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Navigation buttons */}
              <div className={`flex ${currentStep > 1 ? "justify-between" : "justify-end"} mt-8 pt-6 border-t border-gray-100`}>
                {currentStep > 1 && (
                  <button type="button" onClick={goBack} className="px-6 py-3 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-all">
                    ← Back
                  </button>
                )}
                {currentStep < FORM_STEPS.length ? (
                  <button type="button" onClick={goNext} className="flex items-center gap-2 px-7 py-3 bg-[#002301] text-white rounded-xl font-semibold text-sm hover:bg-[#004d02] transition-all">
                    Continue <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button type="button" onClick={handleSubmit} disabled={submitting || !agreedToTos} className={`flex items-center gap-2 px-7 py-3 rounded-xl font-bold text-sm transition-all ${submitting || !agreedToTos ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-[#FFB600] text-[#002301] hover:bg-[#17BC2E] hover:text-white"}`}>
                    {submitting ? "Submitting..." : "Submit Application"}
                    {!submitting && <ArrowRight className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="bg-[#002301] rounded-3xl p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-10">
            <svg width="100%" height="100%"><defs><pattern id="fin-cta" width="25" height="25" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" /></pattern></defs><rect width="100%" height="100%" fill="url(#fin-cta)" /></svg>
          </div>
          <div className="relative z-10">
            <h2 className="text-white font-bold text-2xl sm:text-3xl mb-3">Not Sure Where to Start?</h2>
            <p className="text-white/60 text-base mb-7 max-w-xl mx-auto">Use our free solar calculator to estimate your system size and savings, or browse verified solar providers.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/solar-calculator" className="inline-flex items-center justify-center gap-2 bg-[#FFB600] text-[#002301] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-white hover:text-[#002301] transition-all duration-300">
                Calculate Savings
              </Link>
              <Link href="/marketplace" className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/25 text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-white/20 transition-all duration-300">
                Browse Products
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
